import type { ComponentType } from "react";
import { getImperativeOverlayApi } from "@app/overlay/imperativeOverlayApi";
import { ConfirmOverlayContent } from "./ConfirmOverlayContent";
import type { ConfirmOptions } from "./types";

const CONFIRM_DIALOG_KEY = "app.confirm";

interface ConfirmOverlayContentProps {
    options: ConfirmOptions;
    onResolved: (value: boolean) => void;
}

/**
 * Показывает диалог подтверждения через overlay dialog.
 * Требует смонтированного {@link OverlayProvider}.
 * @returns значение типа `T` при подтверждении, `false` при отмене
 */
function show<T = boolean>(options: ConfirmOptions<T>): Promise<T | false> {
    return new Promise<T | false>((resolve) => {
        let isSettled = false;

        const settle = (value: boolean) => {
            if (isSettled) return;
            isSettled = true;
            resolve(value as T | false);
        };

        const overlayApi = getImperativeOverlayApi();
        overlayApi.openDialog(
            ConfirmOverlayContent as ComponentType<ConfirmOverlayContentProps>,
            {
                options: options as ConfirmOptions,
                onResolved: settle,
            },
            {
                key: CONFIRM_DIALOG_KEY,
                reuse: "replace",
                blocking: true,
                closeOnEscape: true,
                closeOnBackdropClick: true,
                onClosed: () => settle(false),
            },
        );
    });
}

/**
 * Пресет подтверждения удаления.
 */
function confirmDelete(entityName: string): Promise<boolean> {
    return show({
        title: "Подтверждение удаления",
        message: `Вы уверены, что хотите удалить ${entityName}?`,
        confirmText: "Удалить",
        cancelText: "Закрыть",
    });
}

/**
 * Императивный API подтверждений (по аналогии с notification).
 */
export const confirm = {
    show,
    delete: confirmDelete,
} as const;

export type ConfirmAPI = typeof confirm;

export default confirm;
